import { useEffect } from "react";

/**
 * WO 800 §4 — real HTTP statuses for crawler snapshots.
 *
 * Public bot traffic is served by a headless crawler-renderer that snapshots
 * this SPA, so the SPA itself must express the intended status. These are the
 * standard dynamic-rendering meta conventions (Prerender.io et al.):
 *
 *   <meta name="prerender-status-code" content="410">
 *   <meta name="prerender-header" content="Location: https://…">
 *
 * The renderer maps them onto the HTTP response. Harmless for real browsers.
 */
export default function PrerenderStatus({ code, location }) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.head
      .querySelectorAll('meta[name="prerender-status-code"], meta[name="prerender-header"]')
      .forEach((n) => n.remove());
    const status = document.createElement("meta");
    status.setAttribute("name", "prerender-status-code");
    status.setAttribute("content", String(code));
    document.head.appendChild(status);
    let header;
    if (location) {
      header = document.createElement("meta");
      header.setAttribute("name", "prerender-header");
      header.setAttribute("content", `Location: ${location}`);
      document.head.appendChild(header);
    }
    return () => {
      status.remove();
      if (header) header.remove();
    };
  }, [code, location]);
  return null;
}

// Crawler / headless-renderer detection. HeadlessChrome covers the
// crawler-renderer's own browser even when it doesn't forward the bot UA.
const CRAWLER_RE =
  /googlebot|bingbot|slurp|duckduckbot|baiduspider|yandex(?:bot)?|petalbot|applebot|facebookexternalhit|twitterbot|linkedinbot|whatsapp|telegrambot|discordbot|pinterest|semrushbot|ahrefsbot|mj12bot|screaming frog|headlesschrome|prerender/i;

export const isCrawler = () =>
  typeof navigator !== "undefined" && CRAWLER_RE.test(navigator.userAgent || "");
